import {
    ApprovalExpenseGet,
    ApprovalInvoiceGet,
    Decimal,
} from "@kase/my-marathon-communication";
import { ReadonlySignal, computed } from "@preact/signals";
import { store } from "~/core/store";
import { dateFromApi } from "~/utils";
import { ApiSliceParams } from "../interfaces";
import { createEmitter, createListController } from "../utils";
import {
    ApprovalExpense,
    ApprovalInvoice,
    ApproveEntityType,
    ApproveFilter,
    ApproveParams,
    ApprovePrivateFilter,
} from "./interfaces";
import {
    getEntityId,
    getFilterKey,
    getPrivateFilterKey,
    splitCompanies,
} from "./utils";

export const makeApproval = ({ connection }: ApiSliceParams) => {
    const { emit, on, off } = createEmitter<
        ApprovalExpense | ApprovalInvoice
    >();
    const loadingStore: Record<string, ReadonlySignal<boolean>> = {};
    const loadedStore: Record<string, ReadonlySignal<boolean>> = {};
    const loadedAllStore: Record<string, ReadonlySignal<boolean>> = {};
    const {
        items: expenses,
        listen: listenExpenses,
        loading: loadingExpenses,
        loaded: loadedExpenses,
    } = createListController<
        ApprovalExpenseGet,
        ApprovalExpense,
        ApprovePrivateFilter
    >({
        connection,
        list: (con, { status, companies }) =>
            con.approve.expense.approval.itemsBatch({
                status,
                company: companies,
            }),
        getKey: getPrivateFilterKey,
        getId: (e) => getEntityId("expense", e),
        view: (item) => ({
            ...item,
            _id: item.id,
            id: getEntityId("expense", item),
            date: dateFromApi(item.date),
        }),
        emit,
        name: "approvalExpense",
    });

    const {
        items: invoices,
        listen: listenInvoices,
        loading: loadingInvoices,
        loaded: loadedInvoices,
    } = createListController<
        ApprovalInvoiceGet,
        ApprovalInvoice,
        ApprovePrivateFilter
    >({
        connection,
        list: (con, { status, companies }) =>
            con.approve.invoice.approval.itemsBatch({
                status,
                company: companies,
            }),
        getKey: getPrivateFilterKey,
        getId: (e) => getEntityId("invoice", e),
        view: (item) => ({
            ...item,
            _id: item.id,
            id: getEntityId("invoice", item),
            date: dateFromApi(item.date),
        }),
        emit,
        name: "approvalInvoice",
    });

    const serviceCheck = () =>
        [
            store.api.service.enabledPeek("ApprovalInvoice", "List"),
            store.api.service.enabledPeek("ApprovalExpense", "List"),
        ] as const;

    const myExpenses = computed(() =>
        expenses.value.filter(
            (e) => e.user === connection.value.user.preferred_username,
        ),
    );
    const myInvoices = computed(() =>
        invoices.value.filter(
            (i) => i.user === connection.value.user.preferred_username,
        ),
    );
    const myAll = computed(() => [...myExpenses.value, ...myInvoices.value]);
    const mine = {
        expenses: myExpenses,
        invoices: myInvoices,
        all: myAll,
    };
    const all = computed(() => [...expenses.value, ...invoices.value]);

    const listen = (
        filterInvoice: ApprovePrivateFilter,
        filterExpense: ApprovePrivateFilter,
    ) => {
        const cbs: (() => void)[] = [];
        const [invoiceEnabled, expenseEnabled] = serviceCheck();

        if (invoiceEnabled) {
            cbs.push(listenInvoices(filterInvoice));
        }

        if (expenseEnabled) {
            cbs.push(listenExpenses(filterExpense));
        }

        return () => {
            cbs.forEach((cb) => cb());
        };
    };

    const approveInvoice = ({
        id,
        parent,
        company,
        amount,
        comment,
        kind,
    }: ApproveParams) =>
        connection.value.approve.invoice.approval.approve({
            id,
            parent,
            company,
            amount: Decimal.from(amount),
            comment,
            kind,
        });

    const approveExpense = ({
        id,
        parent,
        company,
        amount,
        comment,
        kind,
    }: ApproveParams) =>
        connection.value.approve.expense.approval.approve({
            id,
            parent,
            company,
            amount: Decimal.from(amount),
            comment,
            kind,
        });

    const loading = (filter: ApproveFilter, type: ApproveEntityType) => {
        const key = type + getFilterKey(filter);

        if (!(key in loadingStore)) {
            const [invoiceCompanies, expenseCompanies] = splitCompanies(
                filter.companies,
            );

            if (type === "invoice") {
                const inv = filter.statuses.map((status) =>
                    loadingInvoices({ status, companies: invoiceCompanies }),
                );

                loadingStore[key] = computed(() =>
                    inv.map((i) => i.value).some((v) => v),
                );
            } else {
                const exp = filter.statuses.map((status) =>
                    loadingExpenses({ status, companies: expenseCompanies }),
                );

                loadingStore[key] = computed(() =>
                    exp.map((e) => e.value).some((v) => v),
                );
            }
        }

        return loadingStore[key];
    };

    const loaded = (
        filter: ApproveFilter,
        type: ApproveEntityType,
        api = false,
    ) => {
        const key = type + getFilterKey(filter, api);

        if (!(key in loadedStore)) {
            const [invoiceCompanies, expenseCompanies] = splitCompanies(
                filter.companies,
            );

            if (type === "invoice") {
                const inv = filter.statuses.map((status) =>
                    loadedInvoices(
                        { status, companies: invoiceCompanies },
                        api,
                    ),
                );

                loadedStore[key] = computed(() =>
                    inv.map((i) => i.value).every((v) => v),
                );
            } else {
                const exp = filter.statuses.map((status) =>
                    loadedExpenses(
                        { status, companies: expenseCompanies },
                        api,
                    ),
                );

                loadedStore[key] = computed(() =>
                    exp.map((e) => e.value).every((v) => v),
                );
            }
        }

        return loadedStore[key];
    };

    const loadedAll = (filter: ApproveFilter, api = false) => {
        const key = getFilterKey(filter, api);

        if (!(key in loadedAllStore)) {
            const [invoiceEnabled, expenseEnabled] = serviceCheck();
            const signals: ReadonlySignal<boolean>[] = [];

            if (invoiceEnabled) {
                signals.push(loaded(filter, "invoice", api));
            }

            if (expenseEnabled) {
                signals.push(loaded(filter, "expense", api));
            }

            loadedAllStore[key] = computed(() =>
                signals.map((s) => s.value).every((v) => v),
            );
        }

        return loadedAllStore[key];
    };

    const approve = (params: ApproveParams, type: ApproveEntityType) =>
        type === "invoice" ? approveInvoice(params) : approveExpense(params);

    return {
        expenses,
        invoices,
        all,
        mine,
        listen,
        loading,
        loaded,
        loadedAll,
        approve,
        on,
        off,
    };
};
